import { defineStore } from 'pinia';
import { ref } from 'vue';
import { useDelivererAuthStore } from './auth';
import { db } from '../db/deliverer.db';
import { getEcho, onEchoConnectionChange } from '../lib/echo';
import { playOrderAssignedSound } from '../../../shared/useSound';

const STORAGE_KEY = 'pwa_deliverer_token';

function authHeaders(): Record<string, string> {
    const token = localStorage.getItem(STORAGE_KEY);
    return token
        ? { Authorization: `Bearer ${token}`, Accept: 'application/json', 'Content-Type': 'application/json' }
        : { Accept: 'application/json', 'Content-Type': 'application/json' };
}

export interface EnabledRate {
    card_brand: string;
    transaction_type: string;
}

export interface DelivererOrder {
    id: string;
    number: string;
    status: 'assigned' | 'on_route' | 'arrived';
    channel: string;
    customer_name: string | null;
    arrived_at: string | null;
    customer_phone: string | null;
    delivery_address: {
        line1: string;
        neighborhood: string;
        zip: string;
        lat: number | null;
        lng: number | null;
        full: string;
        note?: string;
        distance_km?: number | null;
    };
    delivery_token_hash: string;
    requires_token: boolean;
    requires_photo: boolean;
    items: { name: string; quantity: number }[];
    payment_method: string;
    payment_amount: number;
    payment_on_delivery: boolean;
    payment_method_type: string | null;
    payment_card_brand: string | null;
    enabled_rates: EnabledRate[];
    change_for: number | null;
    change_amount: number | null;
    assigned_at: string;
}

export const useOrdersStore = defineStore('orders', () => {
    const orders = ref<DelivererOrder[]>([]);
    const loading = ref(false);
    const isOffline = ref(!navigator.onLine);
    const fetchError = ref<string | null>(null);

    // Polling adaptativo: 30s sem WS, 90s com WS conectado (safety net)
    const POLL_FAST_MS = 30_000;
    const POLL_SLOW_MS = 90_000;
    let pollingTimer: ReturnType<typeof setInterval> | null = null;
    let currentPollMs = POLL_FAST_MS;
    let unsubscribeWsState: (() => void) | null = null;
    // Pedidos cujo canal `order.{id}` já foi assinado — evita empilhar listeners
    // (fetchActive reassina a cada poll) e garante que cada pedido tenha exatamente
    // uma assinatura de OrderStatusUpdated.
    const subscribedOrderIds = new Set<string>();

    async function fetchActive(): Promise<void> {
        if (!navigator.onLine) {
            isOffline.value = true;
            orders.value = (await db.deliverer_orders.toArray()) as unknown as DelivererOrder[];
            return;
        }
        isOffline.value = false;
        loading.value = true;
        try {
            const res = await fetch('/api/v1/deliverer/orders', { headers: authHeaders() });
            if (!res.ok) {
                if (res.status === 401) {
                    // Token guardado no localStorage não é mais válido (expirado/revogado) —
                    // desloga para o watch em App.vue redirecionar pro /login.
                    useDelivererAuthStore().logout();
                    return;
                }
                const body = await res.text().catch(() => '');
                console.error(`[orders] fetchActive failed — HTTP ${res.status}`, body);
                fetchError.value = `HTTP ${res.status}`;
                return;
            }
            fetchError.value = null;
            const json = await res.json() as { data: DelivererOrder[] };
            orders.value = json.data;
            // Persist to IndexedDB for offline access
            await db.deliverer_orders.clear();
            for (const o of json.data) {
                await db.deliverer_orders.put({
                    ...o,
                    cached_at: Date.now(),
                });
            }
            subscribeToOrderChannels(orders.value.map(o => o.id));
        } finally {
            loading.value = false;
        }
    }

    async function startRoute(id: string): Promise<void> {
        // Optimistic update
        const idx = orders.value.findIndex(o => o.id === id);
        if (idx !== -1) orders.value[idx].status = 'on_route';

        const res = await fetch(`/api/v1/deliverer/orders/${id}/start-route`, {
            method: 'PATCH',
            headers: authHeaders(),
        });
        if (!res.ok) {
            // Revert
            if (idx !== -1) orders.value[idx].status = 'assigned';
            throw new Error('Falha ao iniciar rota');
        }
    }

    async function arrive(id: string, gpsLat: number | null, gpsLng: number | null, override: boolean): Promise<void> {
        const body: Record<string, unknown> = { override };
        if (gpsLat !== null) body.gps_lat = gpsLat;
        if (gpsLng !== null) body.gps_lng = gpsLng;

        const res = await fetch(`/api/v1/deliverer/orders/${id}/arrive`, {
            method: 'PATCH',
            headers: authHeaders(),
            body: JSON.stringify(body),
        });

        if (!res.ok) {
            const json = await res.json().catch(() => ({})) as Record<string, unknown>;
            // Propaga para o caller poder tratar GPS fora do raio
            const err = Object.assign(new Error((json.message as string) ?? 'Falha ao registrar chegada'), json);
            throw err;
        }

        // Atualiza status local
        const idx = orders.value.findIndex(o => o.id === id);
        if (idx !== -1) {
            const json = await res.json().catch(() => ({})) as { arrived_at?: string };
            orders.value[idx] = { ...orders.value[idx], status: 'arrived', arrived_at: json.arrived_at ?? new Date().toISOString() };
        }
    }

    async function undoArrive(id: string): Promise<void> {
        // Optimistic update
        const idx = orders.value.findIndex(o => o.id === id);
        if (idx !== -1) orders.value[idx] = { ...orders.value[idx], status: 'on_route', arrived_at: null };

        const res = await fetch(`/api/v1/deliverer/orders/${id}/undo-arrive`, {
            method: 'PATCH',
            headers: authHeaders(),
        });

        if (!res.ok) {
            // Revert
            if (idx !== -1) orders.value[idx] = { ...orders.value[idx], status: 'arrived', arrived_at: null };
            throw new Error('Falha ao desfazer chegada');
        }
    }

    function removeOrder(id: string): void {
        orders.value = orders.value.filter(o => o.id !== id);
        db.deliverer_orders.delete(id);
        // Pedido saiu da lista (entregue/cancelado) → libera o canal e o registro de
        // assinatura para não deixar listener pendurado.
        const echo = getEcho();
        if (echo && subscribedOrderIds.has(id)) echo.leave(`order.${id}`);
        subscribedOrderIds.delete(id);
    }

    async function syncOfflineQueue(): Promise<void> {
        const pending = await db.offline_confirmations.where('synced').equals(0 as unknown as boolean).toArray();
        for (const item of pending) {
            try {
                const res = await fetch(`/api/v1/deliverer/orders/${item.id}/confirm-delivery`, {
                    method: 'POST',
                    headers: authHeaders(),
                    body: JSON.stringify({
                        ...(item.token ? { token: item.token } : {}),
                        offline:      true,
                        confirmed_at: item.confirmed_at,
                        gps_lat:      item.gps_lat,
                        gps_lng:      item.gps_lng,
                    }),
                });
                if (res.ok) {
                    await db.offline_confirmations.update(item.localId!, { synced: true });
                    removeOrder(item.id);
                } else {
                    console.warn(`[sync] Falha ao sincronizar pedido ${item.id}`);
                }
            } catch {
                // Will retry on next reconnect
            }
        }
    }

    function scheduleTick(): void {
        if (pollingTimer) clearInterval(pollingTimer);
        pollingTimer = setInterval(() => {
            if (!document.hidden) fetchActive();
        }, currentPollMs);
    }

    function startPolling(): void {
        fetchActive();
        currentPollMs = POLL_FAST_MS;
        scheduleTick();

        unsubscribeWsState = onEchoConnectionChange(connected => {
            const next = connected ? POLL_SLOW_MS : POLL_FAST_MS;
            if (next === currentPollMs) return;
            currentPollMs = next;
            if (pollingTimer) scheduleTick();
        });

        document.addEventListener('visibilitychange', onVisibilityChange);
        window.addEventListener('online', onOnline);
    }

    function stopPolling(): void {
        if (pollingTimer) {
            clearInterval(pollingTimer);
            pollingTimer = null;
        }
        unsubscribeWsState?.();
        unsubscribeWsState = null;
        document.removeEventListener('visibilitychange', onVisibilityChange);
        window.removeEventListener('online', onOnline);
    }

    function onVisibilityChange(): void {
        if (!document.hidden) fetchActive();
    }

    function onOnline(): void {
        isOffline.value = false;
        fetchActive();
        syncOfflineQueue();
    }

    function subscribeToDelivererChannel(delivererId: string): void {
        const echo = getEcho();
        if (!echo) return;

        echo.private(`deliverer.${delivererId}`)
            .listen('.OrderAssignedToDeliverer', async (payload: unknown) => {
                const order = payload as DelivererOrder;
                orders.value.push(order);
                // Assina o canal do pedido recém-atribuído para receber updates de status
                // em tempo real (ex.: operador clica "Enviar para rota" → OrderStatusUpdated).
                // Sem isso, o card só atualizaria no próximo fetch/refresh — com o polling
                // lento (90s) quando o WS está conectado, ficava preso até refresh manual.
                subscribeToOrderChannels([order.id]);
                playOrderAssignedSound();
                await db.deliverer_orders.put({ ...order, cached_at: Date.now() });
            });
    }

    function subscribeToOrderChannels(orderIds: string[]): void {
        const echo = getEcho();
        if (!echo) return;

        for (const orderId of orderIds) {
            // Já assinado → não re-registra o listener (evita disparo duplicado).
            if (subscribedOrderIds.has(orderId)) continue;
            subscribedOrderIds.add(orderId);
            echo.private(`order.${orderId}`)
                .listen('.OrderStatusUpdated', (payload: { status: string }) => {
                    if (['delivered', 'cancelled'].includes(payload.status)) {
                        removeOrder(orderId);
                    } else {
                        const idx = orders.value.findIndex(o => o.id === orderId);
                        if (idx !== -1) orders.value[idx] = { ...orders.value[idx], status: payload.status as DelivererOrder['status'] };
                    }
                });
        }
    }

    function unsubscribeFromOrderChannels(orderIds: string[]): void {
        const echo = getEcho();
        if (!echo) return;
        for (const orderId of orderIds) {
            echo.leave(`order.${orderId}`);
            subscribedOrderIds.delete(orderId);
        }
    }

    return { orders, loading, isOffline, fetchError, fetchActive, startRoute, arrive, undoArrive, removeOrder, syncOfflineQueue, startPolling, stopPolling, subscribeToDelivererChannel, subscribeToOrderChannels, unsubscribeFromOrderChannels };
});
